import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { log } from '@/logger/logger';
import { copyImage } from '@/utils/files';
import { useGlobalStore } from '@/state/global.state';
import { useResolvedAssets, useWorkflowStore } from '@/state/workflow.state';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import { I18N_KEYS } from '@/language/keys';
import { pngDataUrlToBlob, type MarkedPicture } from '@/components/Recipe/NodeAssetDownloader/markRenderedPicture';
import { isMediaAsset } from './utils';
import type { UploadedAsset } from '@/types/api/assets';
import type { DownloadableAsset } from '@/types/downloadable';
import type { FileKind } from '@/designer/designer';

export interface ImageActions {
  handleDownload: (index: number) => Promise<void>;
  handleDownloadAll: () => Promise<void>;
  handleDownloadImage?: (index: number) => Promise<void>;
  handleDownloadAllImages?: () => Promise<void>;
  handleCopyImage: (index: number) => Promise<void>;
}

interface ImageActionsOptions {
  handleContextMenuClose: () => void;
  images: (UploadedAsset | FileKind)[];
  nodeName: string;
  /** Renders the 3D generation at `index` to a PNG, signed when a model node is upstream. Resolves undefined on failure. */
  renderImage?: (index: number) => Promise<MarkedPicture | undefined>;
}

const logger = log.getLogger('ImageActions');

export const useImageActions = ({
  handleContextMenuClose,
  images,
  nodeName,
  renderImage,
}: ImageActionsOptions): ImageActions => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);
  const recipeName = useWorkflowStore((state) => state.recipe.name);
  const resolvedAssets = useResolvedAssets();
  const { downloadAssetFromNode, downloadAssetsFromNodeWithProgress } = useInteractiveDownloader();
  const isRenderingImagesRef = useRef(false);

  const showRenderError = useCallback(() => {
    updateSnackbarData({
      text: t(I18N_KEYS.RECIPE_MAIN.ERRORS.DOWNLOAD_FAILED),
      isOpen: true,
      severity: 'error',
      action: null,
      duration: 5000,
    });
  }, [t, updateSnackbarData]);
  const handleDownload = useCallback(
    async (index: number) => {
      const file = images[index];
      if (isMediaAsset(file)) {
        track(
          'node_image_download_clicked',
          {
            asset_type: file.type,
            download_kind: file.type === '3D' ? 'model' : 'asset',
            scope: 'current',
            asset_count: 1,
          },
          TrackTypeEnum.BI,
        );
        await downloadAssetFromNode({
          url: file.url,
          fileType: file.type,
          nodeName,
          prefix: recipeName,
          blobstore: file.blobstore,
          mime: file.mime,
        });
      }
      handleContextMenuClose();
    },
    [handleContextMenuClose, images, nodeName, track, recipeName, downloadAssetFromNode],
  );

  const handleDownloadAll = useCallback(async () => {
    track('node_image_download_all_clicked', {}, TrackTypeEnum.BI);
    await downloadAssetsFromNodeWithProgress(images.filter(isMediaAsset), nodeName, recipeName, undefined);
    handleContextMenuClose();
  }, [handleContextMenuClose, images, nodeName, track, recipeName, downloadAssetsFromNodeWithProgress]);

  const handleDownloadImage = useCallback(
    async (index: number) => {
      if (!renderImage || isRenderingImagesRef.current) return;
      const file = images[index];
      if (file?.type !== '3D') return;

      isRenderingImagesRef.current = true;
      try {
        const picture = await renderImage(index);
        if (!picture) throw new Error(`Failed to render 3D generation ${index}`);
        track(
          'node_image_download_clicked',
          { asset_type: '3D', download_kind: 'image', scope: 'current', asset_count: 1 },
          TrackTypeEnum.BI,
        );
        await downloadAssetFromNode({
          ...picture,
          fileType: 'image',
          nodeName,
          prefix: recipeName,
        });
      } catch (error) {
        logger.error('Failed to render 3D image for download:', error);
        showRenderError();
      } finally {
        isRenderingImagesRef.current = false;
        handleContextMenuClose();
      }
    },
    [images, downloadAssetFromNode, handleContextMenuClose, nodeName, recipeName, renderImage, showRenderError, track],
  );

  const handleDownloadAllImages = useCallback(async () => {
    if (!renderImage || isRenderingImagesRef.current) return;
    if (!images.some((file) => file.type === '3D')) return;

    isRenderingImagesRef.current = true;
    try {
      const renderedImages: DownloadableAsset[] = [];
      for (const [index, file] of images.entries()) {
        if (file.type !== '3D') continue;
        const picture = await renderImage(index);
        if (!picture) throw new Error(`Failed to render 3D generation ${index}`);
        renderedImages.push({
          type: 'image',
          blob: 'blob' in picture ? picture.blob : pngDataUrlToBlob(picture.url),
          extension: 'png',
        });
      }

      track(
        'node_image_download_all_clicked',
        { asset_type: '3D', download_kind: 'image', scope: 'all', asset_count: renderedImages.length },
        TrackTypeEnum.BI,
      );
      await downloadAssetsFromNodeWithProgress(renderedImages, nodeName, recipeName, undefined);
    } catch (error) {
      logger.error('Failed to render 3D images for download:', error);
      showRenderError();
    } finally {
      isRenderingImagesRef.current = false;
      handleContextMenuClose();
    }
  }, [
    images,
    downloadAssetsFromNodeWithProgress,
    handleContextMenuClose,
    nodeName,
    recipeName,
    renderImage,
    showRenderError,
    track,
  ]);

  const handleCopyImage = useCallback(
    async (index: number) => {
      track('node_image_copy_clicked', {}, TrackTypeEnum.BI);
      try {
        const file = images[index];
        if (file?.type === 'image') {
          await copyImage(file, resolvedAssets);
        }
      } catch (error) {
        logger.error('Failed to copy image:', error);
      }
      handleContextMenuClose();
    },
    [handleContextMenuClose, images, resolvedAssets, track],
  );

  return {
    handleDownload,
    handleDownloadAll,
    handleDownloadImage: renderImage ? handleDownloadImage : undefined,
    handleDownloadAllImages: renderImage ? handleDownloadAllImages : undefined,
    handleCopyImage,
  };
};
